import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { ORDER_STOP_TYPES } from '../../../constants';
import { BUTTON_LABELS } from '../../../constants/buttonLabels';
import { useDataForSigning } from '../../../hooks/useDataForSigning';
import { useHandleExpiredSigner } from '../../../hooks/useHandleExpiredSigner';
import { useModalState } from '../../../hooks/useModalState';
import { useOrderMutation } from '../../../hooks/useOrderMutation';
import { ProductType } from '../../../hooks/useProducts';
import { useHasSlTpError } from '../../../hooks/useSlTpHooks';
import { formatNumberWithDecimals } from '../../../lib/formatters';
import { localStorageKeys } from '../../../lib/localstorageKeys';
import { isMultipleOf } from '../../../lib/math';
import { validateOrderFormData } from '../../../lib/orderHelpers';
import { OrderSide } from '../../../types/generated';
import { ValidatedOrder } from '../../../types/orders';
import { OnboardedButton } from '../../OnboardedButton/OnboardedButton';
import { useTradeFormAmountError } from '../hooks/useTradeFormAmountError';
import { PlaceOrderButton } from './PlaceOrderButton';
import { useTradeFormStore } from './TradeFormState';

export const TradeFormPrimaryButtonUi: React.FC<{
  product: ProductType;
  onPlaceOrder: (orderData: ValidatedOrder) => void;
  isSubmitting: boolean;
  hasRequiredDataErrors: boolean;
  amountValid: boolean;
  tpSlError: boolean;
  alternateLabel: string | undefined;
  signerExpired: boolean;
}> = ({
  isSubmitting,
  product,
  onPlaceOrder,
  hasRequiredDataErrors,
  amountValid,
  tpSlError,
  alternateLabel,
  signerExpired,
}) => {
  const limitPrice = useTradeFormStore((s) => s.limitPrice);

  const priceValidationError = (() => {
    if (limitPrice && limitPrice <= 0n) {
      return 'Price must be greater than 0';
    }
    if (limitPrice && !isMultipleOf(limitPrice, product.tickSize)) {
      return `Price must be a multiple of ${formatNumberWithDecimals(product.tickSize)}`;
    }
    return '';
  })();
  const disabled = signerExpired
    ? false
    : Boolean(
        priceValidationError || isSubmitting || hasRequiredDataErrors || !amountValid || tpSlError
      );
  return (
    <div className="w-full">
      {hasRequiredDataErrors && (
        <p className="flex items-center gap-1 text-xs text-contentRed">
          <InformationCircleIcon width={16} height={16} className="shrink-0" /> Error loading
          required order data. Our team has been notified and working on a fix.
        </p>
      )}

      <OnboardedButton className="w-full">
        <PlaceOrderButton signerExpired={signerExpired} disabled={disabled} onClick={onPlaceOrder}>
          {signerExpired
            ? BUTTON_LABELS.action.renewSigner
            : alternateLabel || BUTTON_LABELS.action.placeOrder}
        </PlaceOrderButton>
      </OnboardedButton>
    </div>
  );
};
export const TradeFormPrimaryButton: React.FC<{
  product: ProductType;
  alternateLabel?: string;
}> = ({ product, alternateLabel }) => {
  const { error: signingDataError } = useDataForSigning();
  const { signerExpired, withSignerCheck } = useHandleExpiredSigner();
  const setShowConfirmOrderModal = useModalState((state) => state.setConfirmOrderModal);

  const confirmOrderModal = useModalState((state) => state.confirmOrderModal);
  const [showConfirmOrderModal] = useLocalStorage(localStorageKeys.showConfirmOrderModal, true);
  const { valid: amountValid } = useTradeFormAmountError(product);
  const submitOrderMutation = useOrderMutation(product);
  const side = useTradeFormStore((state) => state.side);
  const stopLoss = useTradeFormStore((state) => state.stopLoss);
  const takeProfit = useTradeFormStore((state) => state.takeProfit);
  const showTpSl = useTradeFormStore((state) => state.showTpSl);

  const { hasStopLossError, hasTakeProfitError } = useHasSlTpError({
    takeProfitPrice: takeProfit,
    stopLossPrice: stopLoss,
    productId: product.id,
    side,
  });
  const handlePlaceOrder = useCallback(
    async (orderData: ValidatedOrder) => {
      const wrappedAction = withSignerCheck(async () => {
        if (showConfirmOrderModal && confirmOrderModal === 'closed') {
          setShowConfirmOrderModal('open');
        } else {
          const data = await submitOrderMutation.mutateAsync(orderData);

          const otocoGroupId = data.data.otocoGroupId;
          if (orderData.discriminator === 'OtoLimitOrder' && otocoGroupId) {
            const sharedParams = {
              type: 'MARKET',
              side: orderData.side === OrderSide.BUY ? OrderSide.SELL : OrderSide.BUY,
              reduceOnly: true,
              close: false,
              otocoGroupId,
              amount: orderData.amount,
            } as const;

            if (orderData.takeProfit) {
              const tpOrderData = validateOrderFormData({
                ...sharedParams,
                stopPrice: orderData.takeProfit,
                stopType: ORDER_STOP_TYPES.GAIN,
              });
              if (tpOrderData) {
                submitOrderMutation.mutate(tpOrderData);
              }
            }

            if (orderData.stopLoss) {
              const slOrderData = validateOrderFormData({
                ...sharedParams,
                stopPrice: orderData.stopLoss,
                stopType: ORDER_STOP_TYPES.LOSS,
              });
              if (slOrderData) {
                submitOrderMutation.mutate(slOrderData);
              }
            }
          }
        }
      });
      await wrappedAction();
    },
    [
      withSignerCheck,
      showConfirmOrderModal,
      confirmOrderModal,
      setShowConfirmOrderModal,
      submitOrderMutation,
    ]
  );

  return (
    <TradeFormPrimaryButtonUi
      onPlaceOrder={handlePlaceOrder}
      isSubmitting={submitOrderMutation.isPending}
      hasRequiredDataErrors={Boolean(signingDataError)}
      amountValid={amountValid}
      product={product}
      tpSlError={showTpSl && (hasStopLossError || hasTakeProfitError)}
      signerExpired={signerExpired}
      alternateLabel={alternateLabel}
    />
  );
};
